"use client";

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import type { AffiliateItem } from "@/lib/affiliate";

function shuffle(input: AffiliateItem[]): AffiliateItem[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Static grid classes per item count (Tailwind needs the full class names).
const GRID_BY_COUNT: Record<number, string> = {
  3: "grid-cols-1 sm:grid-cols-3",
  4: "grid-cols-2 lg:grid-cols-4",
};

export function AffiliateCarousel({
  items,
  count = 3,
}: {
  items: AffiliateItem[];
  count?: number;
}) {
  // Render the server order on first paint (avoids hydration mismatch), then
  // pick random items on the client so each visit shows a different set.
  const [picks, setPicks] = useState(() => items.slice(0, count));
  useEffect(() => {
    setPicks(shuffle(items).slice(0, count));
  }, [items, count]);

  if (picks.length === 0) return null;

  return (
    <div className={`grid gap-4 ${GRID_BY_COUNT[count] ?? GRID_BY_COUNT[3]}`}>
      {picks.map((item) => (
        <a
          key={item.id}
          href={item.url}
          target="_blank"
          rel="sponsored nofollow noopener"
          onClick={() =>
            posthog.capture?.("affiliate_click", {
              item_id: item.id,
              item_name: item.name,
              url: item.url,
            })
          }
          className="group flex flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition hover:shadow-md hover:border-primary/40"
        >
          <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
            {item.image_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={item.image_url}
                alt={item.name}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                No image
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-1 p-3">
            <h3 className="text-sm font-semibold leading-snug line-clamp-2">{item.name}</h3>
            {item.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
            )}
            <span className="mt-auto pt-2 text-sm font-medium text-primary group-hover:underline">
              {item.cta_label} →
            </span>
          </div>
        </a>
      ))}
    </div>
  );
}
